import { useCallback, useEffect, useReducer, useState } from 'react'
import { useAuthContext } from './AuthContext'
import { CreateBatchReportRequestInput } from 'schema'
import { createStrictContext } from 'utils/createContext'
import { cimsStorage } from '../utils/storage'

/**
 * application global state variables and methods
 *
 * for each field you add to the {@link GlobalState.state|state}, you should also
 * add an associated {@link GlobalState.dispatch|update} function
 *
 * soft todo...
 * - flatten global state
 * - use react nomenclature for state/update. i.e. projectGlobalId/setProjectGlobalId
 */
interface GlobalState {
  /**
   * global variables which exist above the application, but below the the
   * auth layer
   */
  state: InitialState & {
    /**
     * the base SharePoint site url associated with the project
     */
    sharePointSiteUrl?: string
  }
  /**
   * methods to update the global state
   */
  dispatch: {
    /**
     * updates the {@link InitialState.projectGlobalId|projectGlobalId} state variable
     *
     * also updates sharePointSiteUrl
     *
     * @param projectGlobalId
     */
    updateProjectGlobalId: (projectGlobalId?: string) => void
    /**
     * updates the {@link InitialState.navKey|navKey} state variable
     *
     * @param navKey
     */
    updateNavKey: (navKey?: string) => void
    /**
     * updates the {@link InitialState.showGroups|showGroups} state variable
     *
     * @param showGroups
     */
    updateShowGroups: (showGroups?: Map<string, boolean>) => void
    /**
     * updates the {@link InitialState.createBatchReportRequestInput|createBatchReportRequestInput} state variable
     *
     * @param input
     */
    updateCreateBatchReportRequestInput: (
      input?: GlobalState['state']['createBatchReportRequestInput']
    ) => void
  }
}

interface InitialState {
  /**
   * the `globalId` of the currently selected `Project`
   */
  projectGlobalId?: string
  /**
   * the currently selected sidebar key
   */
  navKey?: string
  /**
   * which pay item connection groups to collapse/expand
   */
  showGroups?: Map<string, boolean>
  /**
   * the current filter options for standard batch exports
   */
  createBatchReportRequestInput?: Partial<CreateBatchReportRequestInput>
}
type Dispatch = GlobalState['dispatch']

const initialState: InitialState = {
  projectGlobalId: cimsStorage.projectId,
  navKey: cimsStorage.pageId,
  showGroups: cimsStorage.visiblePayItemConnectionGroups,
  createBatchReportRequestInput: cimsStorage.createBatchReportRequestInput,
}

const { Provider, useContextFn } =
  createStrictContext<GlobalState>('GlobalStateContext')

/**
 * a generic interface used to help define which dispatch actions are available
 */
interface Update<Payload> {
  /**
   * the name of the update type
   *
   * while it doesn't have to be... it should be UPPER_SNAKE_if (field === '')
   * @example
   * 'GLOBAL_STATE_FIELD'
   */
  field: keyof InitialState
  /**
   * the payload that will be used in the dispatch function to update the state
   */
  payload?: Payload
}

/**
 * a callback used in the {@link GlobalStateProvider} to help resolve each
 * potential dispatch event
 *
 * @param state the {@link initialState} of the {@link GlobalState}
 * @param updatePayload {@link UpdatePayload}
 */
const reducer = (
  state: InitialState,
  update:
    | Update<string>
    | Update<Map<string, boolean>>
    | Update<Partial<CreateBatchReportRequestInput>>
): InitialState => {
  const { field, payload } = update
  if (field === 'projectGlobalId')
    return {
      ...state,
      projectGlobalId: payload as InitialState['projectGlobalId'],
    }
  else if (field === 'navKey')
    return { ...state, navKey: payload as InitialState['navKey'] }
  else if (field === 'showGroups')
    return { ...state, showGroups: payload as InitialState['showGroups'] }
  else if (field === 'createBatchReportRequestInput')
    return {
      ...state,
      createBatchReportRequestInput:
        payload as InitialState['createBatchReportRequestInput'],
    }
  else return state
}

export const GlobalStateProvider: React.FC = ({ children }) => {
  const [state, dispatch] = useReducer(reducer, initialState)
  const { user } = useAuthContext()
  const [sharePointSiteUrl, setSharePointSiteUrl] = useState<
    string | undefined
  >(undefined)
  useEffect(() => {
    setSharePointSiteUrl(
      user?.projects?.find((project) => project.id === state.projectGlobalId)
        ?.sharePoint.site?.url
    )
  }, [state.projectGlobalId, user?.projects])

  const updateNavKey = useCallback<Dispatch['updateNavKey']>(
    (payload) => {
      if (payload === state.navKey) return
      cimsStorage.setPageId(payload)
      dispatch({ field: 'navKey', payload })
    },
    [state.navKey]
  )
  const updateProjectGlobalId = useCallback<Dispatch['updateProjectGlobalId']>(
    (payload) => {
      if (payload === state.projectGlobalId) return
      cimsStorage.setProjectId(payload)
      dispatch({ field: 'projectGlobalId', payload })
    },
    [state.projectGlobalId]
  )
  const updateShowGroups = useCallback<Dispatch['updateShowGroups']>(
    (payload) => {
      cimsStorage.setVisiblePayItemConnectionGroups(payload)
      dispatch({ field: 'showGroups', payload })
    },
    []
  )
  const updateCreateBatchReportRequestInput = useCallback<
    Dispatch['updateCreateBatchReportRequestInput']
  >((payload) => {
    cimsStorage.setCreateBatchReportRequestInput(payload)
    dispatch({ field: 'createBatchReportRequestInput', payload })
  }, [])
  return (
    <Provider
      value={{
        state: { ...state, sharePointSiteUrl },
        dispatch: {
          updateProjectGlobalId,
          updateNavKey,
          updateShowGroups,
          updateCreateBatchReportRequestInput,
        },
      }}
    >
      {children}
    </Provider>
  )
}

/**
 * gives access to the application's {@link GlobalState}
 */
export const useGlobalState = useContextFn
